import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { generatePath, useHistory } from 'react-router-dom';

import { redirects } from '../../constants/redirects';
import { MapBounds } from '../../containers/MapContainer/MapContainer';
import { GlobalMessages } from '../../types/Messages';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import { messageHooks } from '../messageHooks/messageHooks';
import { routerBuildHooks } from '../routerBuildHooks/routerBuildHooks';
import { routerHooks } from '../routerHooks/routerHooks';

export type usePOPRedirectParams = {
  isNextPage?: boolean;
  isPOP?: boolean;
  page?: number;
  preserveQueryParams?: boolean;
};

export type UseCOPNextRedirectParams = {
  url: string;
};

export type PrimitiveType = string | number | boolean | null | undefined | Date;

export type FormatXMLElementFn<T, R = string | T | Array<string | T>,> = (
  parts: Array<string | T>) => R;

export type UseRedirectHandlerParams = {
  isGeneratePath?: boolean;
  isRedeem?: boolean;
  url: MessageDescriptor;
  values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>;
};

export const navigationHooks = {
  useGetLanguage(isRedeem?: boolean) {
    const language = messageHooks.useSubjectState(GlobalMessages.Language);
    const defaultLanguage = languageUtils.getDefaultLanguage();

    const languageToUse = isRedeem && language !== defaultLanguage
      ? `/${defaultLanguage}`
      : `/${language}`;

    return !language || language === defaultLanguage
      ? ''
      : languageToUse;
  },
  useRedirectHandlerWithInnerParams(isRedeem?: boolean) {
    const intl = useIntl();
    const language = navigationHooks.useGetLanguage(isRedeem);
    const handleRedirectWithInnerParams = React.useCallback(
      ({
        url,
        values,
        isGeneratePath,
      }: UseRedirectHandlerParams) => {
        if (isGeneratePath) {
          return `${language}${generatePath(intl.formatMessage(url, values))}`;
        }
        return `${language}${intl.formatMessage(url, values)}`;
      }, [intl, language],
    );
    return {
      handleRedirectWithInnerParams,
    };
  },
  useRedirectHandler({
    url,
    values,
    isGeneratePath,
    isRedeem,
  }: UseRedirectHandlerParams) {
    const intl = useIntl();
    const language = navigationHooks.useGetLanguage(isRedeem);
    const handleRedirect = React.useCallback(
      () => {
        if (isGeneratePath) {
          return `${language}${generatePath(intl.formatMessage(url, values))}`;
        }
        return `${language}${intl.formatMessage(url, values)}`;
      }, [intl, isGeneratePath, language, url, values],
    );

    return {
      redirectUrl: handleRedirect(),
      handleRedirect,
    };
  },
  useHomePageRedirect(isRedeem?: boolean) {
    const {
      handleRedirect,
    } = navigationHooks.useRedirectHandler({
      url: redirects.homePageRedirect,
      isRedeem,
    });
    return React.useCallback((hash?: string) => {
      let url = handleRedirect();
      if (hash) {
        url += hash;
      }

      return url;
    }, [handleRedirect]);
  },
  useOldCBRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemLogoRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useBasketPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.basketPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useBlogRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.blogPopRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useBusinessPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.businessPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },

  useCheckoutPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.checkoutPageRedirect,
    });
    return React.useMemo(() => redirectUrl, [redirectUrl]);
  },
  useRedeemCheckoutPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemnCheckoutPageRedirect,
    });
    return React.useMemo(() => redirectUrl, [redirectUrl]);
  },
  usePOPRedirect(params?: usePOPRedirectParams) {
    const { preserveQueryParams, page } = params || {};
    const search = routerHooks.useGetQueryString();
    const {
      redirectUrl: path,
    } = navigationHooks.useRedirectHandler({
      url: redirects.popRedirect,
      values: {
        page: !isNaN(Number(page)) ? page : 1,
      },
    });
    return React.useCallback(() => {
      return preserveQueryParams ? path + search : path;
    }, [path, preserveQueryParams, search]);
  },
  useCOPRedirect(preserveQueryParams?: boolean, page?: number) {
    const search = routerHooks.useGetQueryString();
    const params = routerBuildHooks.usePageParams<UseCOPNextRedirectParams>();
    const copNextParams = routerBuildHooks.useCOPNextParams();
    const url = copNextParams?.slug ? copNextParams.slug[0] : params.url;
    const {
      redirectUrl: path,
    } = navigationHooks.useRedirectHandler({
      url: redirects.copRedirect,
      values: {
        page: !isNaN(Number(page)) ? page : 1,
        url,
      },
    });
    return React.useCallback(() => {
      return preserveQueryParams ? path + search : path;
    }, [preserveQueryParams, path, search]);
  },
  useRedeemCheckBalanceRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useRedeemShopPageRedirect(page: number) {
    const {
      redirectUrl: path,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemShopPageRedirect,
      values: { page },
    });
    const search = routerHooks.useSearch();

    return React.useCallback(() => {
      return path + search;
    }, [path, search]);
  },
  useRedeemB2bShopPageRedirect(pageId: string, page: number) {
    const {
      redirectUrl: path,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemB2BShopPageRedirect,
      values: { pageId, page },
    });
    const search = routerHooks.useSearch();

    return React.useCallback(() => {
      return path + search;
    }, [path, search]);
  },
  usePrivacyPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.privacyPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useTermsAndConditionsPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.termsAndConditionsRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useRedeemPrivacyPageRedirect(isRedeem?: boolean) {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemPrivacyRedirect,
      isRedeem,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useRedeemTermsAndConditionsPageRedirect(isRedeem?: boolean) {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemTermsAndConditionsRedirect,
      isRedeem,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useDoublerPage() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.doublerHomePageRedirect,
    });
    return React.useMemo(() => redirectUrl, [redirectUrl]);
  },
  useCustomerSupportPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.helpdeskRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useOldCustomerSupportPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.redeemClientServiceRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useNotFoundPageRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.notFoundPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountIdentifyRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.accountIdentifyPageRedirect,
    });
    return React.useMemo(() => redirectUrl, [redirectUrl]);
  },
  useAccountRegisterRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.accountRegisterPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountRegisterSuccessRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.accountRegisterSuccessPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountLoginRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.loginPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountForgotPasswordRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.forgotPasswordPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountForgotPasswordConfirmRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.forgotPasswordConfirmPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountResetPasswordRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.newPasswordPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountResetPasswordConfirmRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.newPasswordConfirmationPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountDetailsRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.accountDataPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useAccountOverviewRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.accountOverviewPageRedirect,
    });
    return React.useMemo(() => redirectUrl, [redirectUrl]);
  },
  useAccountOrdersPageRoute() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.accountOrdersPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useLocationsOverviewPageRedirect(value: string) {
    return React.useCallback(() => `${value}`, [value]);
  },
  useCoordinateRectangle() {
    const query = routerHooks.useQueryParams();
    const [params, setParams] = React.useState<Record<string, string | undefined>>();

    React.useEffect(() => {
      setParams(query);
    }, [query]);

    return React.useMemo(() => {
      if (params) {
        const { latmax, latmin, lonmax, lonmin } = params;

        return latmax && latmin && lonmax && lonmin
          ? {
            latmax: parseFloat(latmax),
            latmin: parseFloat(latmin),
            lonmax: parseFloat(lonmax),
            lonmin: parseFloat(lonmin),
          }
          : null;
      }
      return null;
    }, [params]);
  },
  useSetCoordinateRectangle() {
    const search = routerHooks.usePathName();
    const history = useHistory();

    const setCoordinateRectangle = React.useCallback((
      { latmax, latmin, lonmax, lonmin }: MapBounds,
    ) => {
      const queryParams = new URLSearchParams(search);

      if (queryParams.has('page')) {
        queryParams.delete('page');
      }

      if (queryParams.has('s')) {
        queryParams.delete('s');
      }

      if (latmax && latmin && lonmax && lonmin) {
        queryParams.set('latmax', latmax.toFixed(5).toString());
        queryParams.set('latmin', latmin.toFixed(5).toString());
        queryParams.set('lonmax', lonmax.toFixed(5).toString());
        queryParams.set('lonmin', lonmin.toFixed(5).toString());
      }

      history.push({
        search: queryParams.toString(),
      });
    }, [search, history]);

    return { setCoordinateRectangle };
  },
  useLocationDetailsPageRedirect(product: string, location: string) {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.locationDetailsPageRedirect,
      values: { product, location },
      isGeneratePath: true,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useReorderPageRoute() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.reorderPageRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useChoiceGiftcardRoute() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.choiceGiftcardRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
  useRedirectCallback(redirect: () => string) {
    const history = useHistory();
    const language = navigationHooks.useGetLanguage();
    return React.useCallback(() => {
      let url = redirect();
      if (!url.startsWith(language)) {
        url = `${language}${redirect()}`;
      }
      if (BUILD_VARIANT === 'react') {
        history.push(url);
      } else {
        location.href = url;
      }
    }, [history, language, redirect]);
  },
  usePromoPopUpRedirect() {
    const {
      redirectUrl,
    } = navigationHooks.useRedirectHandler({
      url: redirects.promoPopUpRedirect,
    });
    return React.useCallback(() => redirectUrl, [redirectUrl]);
  },
};
